// App theme preference: 'light' | 'dark' | 'system'.
//
// Storage keys:
//  - cg-theme         — canonical preference (read by initTheme before first paint).
//  - cg-preview-theme — RESOLVED theme only ('light'|'dark'), mirrored for
//    auth-free preview builds that boot outside the CRA app. Nothing in the
//    CRA app reads it; 'system' can't be mirrored there.

export type ThemePreference = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const PREF_KEY = 'cg-theme'
const PREVIEW_KEY = 'cg-preview-theme'

// Monotonic id so only the LATEST flip cleans up its helper classes (a
// skipped flip's vt.finished rejects and would otherwise strip them mid-run).
let themeFlipSeq = 0
// `fill: 'forwards'` keeps the pseudo-element effect live after it finishes;
// retain it so a later transition cannot inherit the previous final clip.
let activeThemeClipAnimation: Animation | null = null

export const systemTheme = (): ResolvedTheme =>
	window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'

export const resolveTheme = (pref: ThemePreference): ResolvedTheme =>
	pref === 'system' ? systemTheme() : pref

// Dark mode ships behind the 'dark_mode_enabled' PostHog flag. Defaults to
// true so initTheme (which runs before flags resolve) honors the stored
// preference without a light flash for flag-on users; useDarkModeFlag syncs
// the real value here once flags are ready. While the flag is off the stored
// preference reads as 'light' but is NOT overwritten, so it takes effect
// again if the flag comes back.
let darkModeAllowed = true

export const setDarkModeAllowed = (allowed: boolean) => {
	if (darkModeAllowed === allowed) return
	darkModeAllowed = allowed
	applyTheme(getStoredPreference())
}

// Default is 'light': existing users see no change until they opt in.
export const getStoredPreference = (): ThemePreference => {
	if (!darkModeAllowed) return 'light'
	const v = localStorage.getItem(PREF_KEY)
	return v === 'dark' || v === 'system' ? v : 'light'
}

export const applyTheme = (pref: ThemePreference) => {
	document.documentElement.dataset.theme = resolveTheme(pref)
}

const persist = (pref: ThemePreference) => {
	localStorage.setItem(PREF_KEY, pref)
	localStorage.setItem(PREVIEW_KEY, resolveTheme(pref))
}

// Call once from the CRA entry before first render. Applies the stored
// preference and keeps 'system' in sync if the OS theme flips while open.
export const initTheme = () => {
	applyTheme(getStoredPreference())
	// Reader windows are separate platform tabs. Keep their parent document in
	// sync so its CMS iframe receives changes made in the original dashboard.
	window.addEventListener('storage', (event) => {
		if (
			event.storageArea === localStorage &&
			(event.key === PREF_KEY || event.key === null)
		)
			applyTheme(getStoredPreference())
	})
	window
		.matchMedia('(prefers-color-scheme: dark)')
		.addEventListener('change', () => {
			if (getStoredPreference() === 'system') applyTheme('system')
		})
}

// Sets the preference WITH a theme-change animation. `onApply` runs inside the
// same DOM update as the theme flip (pass a flushSync'd state setter so the
// control's own icon is part of the snapshot). Paths, in order:
//  1. prefers-reduced-motion — instant.
//  2. View Transitions API — circular clip reveal from `origin` (the toggle).
//  3. Otherwise — .cg-theme-flip on <html> (App.css), a ~400ms color cross-fade.
export const transitionTheme = (
	pref: ThemePreference,
	opts: {
		origin?: { x: number; y: number }
		onApply?: () => void
	} = {}
) => {
	const { origin, onApply } = opts
	persist(pref)
	const apply = () => {
		applyTheme(pref)
		onApply?.()
	}

	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
		apply()
		return
	}

	const startViewTransition = (
		document as any
	).startViewTransition?.bind(document)

	if (!startViewTransition || !origin) {
		const root = document.documentElement
		root.classList.add('cg-theme-flip')
		apply()
		window.setTimeout(() => root.classList.remove('cg-theme-flip'), 400)
		return
	}

	// Radius that still reaches the farthest corner from `origin`.
	const vw = window.innerWidth
	const vh = window.innerHeight
	const radius = Math.hypot(
		Math.max(origin.x, vw - origin.x),
		Math.max(origin.y, vh - origin.y)
	)

	// PERCENTAGES, NOT PIXELS — load-bearing. `origin` is a viewport-space
	// getBoundingClientRect() reading, but the clip-path resolves against the
	// ::view-transition-* pseudo-element's OWN border box, and those two spaces
	// are not guaranteed to be 1:1. Observed on Chrome 150 / Retina (staging,
	// 2026-07-29): a px clip painted the circle at exactly origin/devicePixelRatio
	// — mid-screen instead of on the toggle — and browser zoom moved it in step
	// with dpr (at 50% zoom, dpr 1, it was correct). The internal mechanism is
	// unconfirmed (length resolution vs. a snapshot group transform); only the
	// dpr-proportional mapping is measured. Percentages resolve against that same
	// box, so any uniform scale of it cancels either way, and where px already
	// worked this is an exact no-op. Per spec a percentage radius resolves
	// against sqrt(w² + h²) / sqrt(2), hence the divisor.
	const pct = (n: number, basis: number) => (n / basis) * 100
	const cx = pct(origin.x, vw)
	const cy = pct(origin.y, vh)
	const r = pct(radius, Math.hypot(vw, vh) / Math.SQRT2)
	const grown = `circle(${r}% at ${cx}% ${cy}%)`
	const collapsed = `circle(0% at ${cx}% ${cy}%)`

	// The circle is DIRECTIONAL (Esteban's call): LIGHT floods OUT of the toggle
	// (sun rising); activating DARK is the same move REVERSED — the old light
	// page drains back INTO the toggle (nightfall). Those are two different
	// animations, not one played backwards —
	//  - light: grow the NEW snapshot over the old. New is above old by default.
	//  - dark:  shrink the OLD (light) snapshot away so the new dark page shows
	//    through underneath. That needs old ABOVE new (.cg-theme-collapse in
	//    App.css). Animating the new one in reverse would just uncover the old
	//    page, i.e. the wrong theme.
	const root = document.documentElement
	const toDark = resolveTheme(pref) === 'dark'

	// `cg-theme-transitioning` (both directions) neutralises backdrop-filter
	// everywhere. A backdrop-filter can't be captured faithfully into a flat
	// view-transition snapshot — its blur samples a backdrop that no longer
	// exists — so the browser paints those elements out-of-band: they flip to
	// the new theme instantly and float ABOVE the circle instead of being
	// carried by it. The backgrounds are opaque, so dropping the blur for the
	// ~500ms flip is invisible. `cg-theme-collapse` (dark only) additionally
	// flips the snapshot stacking so the OLD layer shrinks on top; see App.css.
	// Start every flip from a known-clean slate. A stale cg-theme-collapse
	// (aborted flip, HMR module swap, a thrown startViewTransition, or an older
	// build's leftover in a long-lived tab) stacks old ABOVE new and hides the
	// dark expand under the old page — reset first, then set what THIS flip
	// needs. This makes the next click self-heal any earlier breakage.
	activeThemeClipAnimation?.cancel()
	activeThemeClipAnimation = null
	root.classList.remove('cg-theme-transitioning', 'cg-theme-collapse')
	root.classList.add('cg-theme-transitioning')
	if (toDark) root.classList.add('cg-theme-collapse')
	// A skipped transition's vt.finished rejects and its cleanup would strip
	// these classes from under the NEWER flip still running — only the latest
	// flip may clean up.
	const flipId = ++themeFlipSeq
	const cleanup = () => {
		if (flipId !== themeFlipSeq) return
		activeThemeClipAnimation?.cancel()
		activeThemeClipAnimation = null
		root.classList.remove('cg-theme-transitioning', 'cg-theme-collapse')
	}

	let vt: any
	try {
		vt = startViewTransition(apply)
	} catch {
		// Thrown synchronously (e.g. another transition mid-flight in some
		// engines): the callback never ran — land the theme without animation
		// and don't leave the helper classes stuck.
		cleanup()
		apply()
		return
	}
	vt.ready
		.then(() => {
			if (flipId !== themeFlipSeq) return
			activeThemeClipAnimation = root.animate(
				{ clipPath: toDark ? [grown, collapsed] : [collapsed, grown] },
				{
					duration: 500,
					easing: 'ease-in-out',
					// Hold the final clip until the transition overlay tears down.
					// Without this, WAAPI's default `fill: 'none'` reverts the clip
					// the instant the animation ends — for the collapse (dark) that
					// snaps the light OLD snapshot (z-index 1) back to fully visible
					// for one frame before teardown, a light flash on the way to dark.
					fill: 'forwards',
					pseudoElement: toDark
						? '::view-transition-old(root)'
						: '::view-transition-new(root)'
				}
			)
		})
		.catch(() => {})
	// Must outlive the animation, and must run even if the transition is
	// skipped/aborted — stuck classes would invert the NEXT flip's stacking,
	// while a filled clip animation could clip its old snapshot to zero.
	vt.finished.catch(() => {}).finally(cleanup)
}
